import { useNavigate } from 'react-router-dom';
import { Steps } from 'antd';
import { faCheck, faUser } from '@fortawesome/free-solid-svg-icons';
import { AzDrawer, Sel } from '../../components/az';
import { useUpdateProspect } from '../../queries/prospects';
import { usePhone } from '../../hooks/usePhone';
import { BOARD_STAGES, bandOf, columnOf, daysInStage, earnedSignals, scoreOf, stageForColumn, whenOf } from '../../utils/prospects';
import { Btn, Chip, I } from '../Todos/ui';
import { formatPhone } from '../../utils/callLog';

const Sec = ({ title, children }) => (
  <section className="ud-sec">
    <div className="m-st">{title}</div>
    {children}
  </section>
);

const stamp = (d) => {
  const dt = new Date(d);
  return `${dt.toLocaleDateString('en-US')} · ${dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

const APP_STATUS = {
  'app-sent': ['Sent, waiting on the prospect', 'warn'],
  'app-complete': ['Complete', 'ok'],
  'lease-signed': ['Complete, lease signed', 'ok'],
  'move-in': ['Complete, lease signed', 'ok'],
  won: ['Complete, moved in', 'ok']
};

export default function ProspectDrawer({ prospect: p, onClose }) {
  const navigate = useNavigate();
  const phone = usePhone();
  const update = useUpdateProspect();
  const col = columnOf(p);
  const idx = BOARD_STAGES.findIndex((s) => s.col === col);
  const days = daysInStage(p);
  const daysText = days == null ? '—' : `${days} ${days === 1 ? 'day' : 'days'} in stage`;
  const score = scoreOf(p);
  const band = bandOf(score);
  const signals = earnedSignals(p).filter((f) => f.weight > 0);
  const app = APP_STATUS[col] || ['Not sent yet', 'neutral'];

  const move = (e) => {
    const stage = stageForColumn(e.target.value);
    if (stage && stage !== p.pipelineStage) update.mutate({ id: p._id, pipelineStage: stage, stageAt: new Date().toISOString() });
  };

  return (
    <AzDrawer
      title={p.name}
      eyebrow={`Leasing pipeline · ${BOARD_STAGES[idx]?.label || p.pipelineStage}`}
      sub={[p.interestedProperty, p.phone && formatPhone(p.phone)].filter(Boolean).join(' · ')}
      chips={
        <span className="aa" style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
          <Chip fam="info">{BOARD_STAGES[idx]?.label || p.pipelineStage}</Chip>
          <Chip fam="neutral">{daysText}</Chip>
        </span>
      }
      footer={
        <div className="aa">
          <div className="ud-foot">
            <Btn icon={faUser} onClick={() => { onClose(); navigate(`/contacts/client/${p._id}`); }}>
              Open profile
            </Btn>
          </div>
        </div>
      }
      onClose={onClose}
    >
      <div className="aa">
        <div className="ud-body">
          {phone ? (
            <div className="m-col gap4">
              <div className="m-sub">{`Stage ${Math.max(idx, 0) + 1} of ${BOARD_STAGES.length}`}</div>
              <div className="ac-bar">
                <span style={{ width: `${((Math.max(idx, 0) + 1) / BOARD_STAGES.length) * 100}%` }} />
              </div>
            </div>
          ) : (
            <div className="pl-steps">
              <Steps
                size="small"
                responsive={false}
                labelPlacement="vertical"
                current={Math.max(idx, 0)}
                items={BOARD_STAGES.map((s) => ({ title: s.label }))}
              />
            </div>
          )}

          <div className="m-field inline">
            <label htmlFor="pl2-mv">Move to stage</label>
            <Sel id="pl2-mv" style={{ width: '100%' }} value={col || ''} onChange={move}>
              {BOARD_STAGES.map((s) => (
                <option key={s.col} value={s.col} disabled={!stageForColumn(s.col)}>
                  {s.label}
                </option>
              ))}
            </Sel>
          </div>

          <Sec title="Next step">
            <div className="sm">{whenOf(p)}</div>
            <div className="m-sub">Owner: {p.assignedTo || 'Unassigned'}</div>
          </Sec>

          <Sec title="Application status">
            <div className="m-row">
              <Chip fam={app[1]}>{app[0]}</Chip>
            </div>
          </Sec>

          <Sec title="Screening">
            <div className="m-row">
              {band && <Chip fam={score >= 7 ? 'ok' : score >= 4 ? 'warn' : 'neutral'}>{`${band.label} · ${score.toFixed(1)}`}</Chip>}
            </div>
            {signals.length ? (
              <div className="m-col gap4">
                {signals.map((f) => (
                  <div key={f.key} className="m-sub">
                    <I icon={faCheck} /> {f.label}
                  </div>
                ))}
              </div>
            ) : (
              <div className="m-sub">Screening signals appear as the application progresses.</div>
            )}
          </Sec>

          <Sec title="Timeline">
            {(() => {
              const events = [
                p.stageAt && [`Moved to ${BOARD_STAGES[idx]?.label || p.pipelineStage}`, stamp(p.stageAt)],
                p.createdAt && ['Entered the pipeline', stamp(p.createdAt)]
              ].filter(Boolean);
              return (
                <div className="pl-timeline">
                  <Steps
                    direction="vertical"
                    size="small"
                    progressDot
                    current={events.length}
                    items={events.map((e) => ({ title: e[0], description: e[1] }))}
                  />
                </div>
              );
            })()}
          </Sec>
        </div>
      </div>
    </AzDrawer>
  );
}
